import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import AlertDialog from "./AlertDialog";
import UserList from "./UserList";
import UnionManagement from "./UnionManagement";
import AppendixManagement from "./AppendixManagement";
import PluginsManagement from "./PluginsManagement";
import CompanyProfileManagement from "./CompanyProfileManagement";
const API = "https://erp-xe9u.onrender.com/api";
const TAB_CONFIG = [
  {
    key: "crew",
    label: "Crew",
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  },
  {
    key: "unions",
    label: "Unions",
    icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
  },
  {
    key: "appendices",
    label: "Appendices",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
  {
    key: "plugins",
    label: "Plugins",
    icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z",
  },
];

const ADD_BUTTON_LABELS = {
  crew: "Add User",
  unions: "Add Union",
  appendices: "Add Appendix",
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [stagedUsers, setStagedUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [unions, setUnions] = useState([]);
  const [appendices, setAppendices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("crew");
  const [currentUser, setCurrentUser] = useState(null);

  const [isQuickBooksConnected, setIsQuickBooksConnected] = useState(false);
  const [isAutomatedBillingConnected, setIsAutomatedBillingConnected] =
    useState(false);
  const [billingAlias, setBillingAlias] = useState(null);
  const [isAutomatedLeadsConnected, setIsAutomatedLeadsConnected] =
    useState(false);
  const [leadsAlias, setLeadsAlias] = useState(null);
  const [leadSources, setLeadSources] = useState([]);

  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
    showCopyButton: false,
  });
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isAddingUnion, setIsAddingUnion] = useState(false);
  const [isAddingAppendix, setIsAddingAppendix] = useState(false);
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [currentUnionPage, setCurrentUnionPage] = useState(1);
  const [currentAppendixPage, setCurrentAppendixPage] = useState(1);
  const [allProjects, setAllProjects] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentUser(response.data.user);
    } catch (err) {
      console.error("Error fetching current user:", err);
    }
  };

  const fetchAllProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/mobile/projects/select-list?limit=200`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAllProjects(response.data.projects || []);
    } catch (err) {
      console.error("Error fetching projects for assignment:", err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
    fetchAccounts();
    fetchUnions();
    fetchAppendices();
    fetchAllProjects();
    fetchQuickBooksStatus();
    fetchAutomatedBillingStatus();
    fetchAutomatedLeadsStatus();
    if (location.pathname.includes("/quickbooks-success")) {
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "QuickBooks connected successfully!",
        onConfirm: () => {
          setDialog((p) => ({ ...p, open: false }));
          setIsQuickBooksConnected(true);
          navigate("/user-management", { replace: true });
        },
        showCopyButton: false,
      });
      fetchQuickBooksStatus();
    } else if (location.pathname.includes("/quickbooks-error")) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to connect QuickBooks. Please try again.",
        onConfirm: () => {
          setDialog((p) => ({ ...p, open: false }));
          navigate("/user-management", { replace: true });
        },
        showCopyButton: false,
      });
    }
  }, [location.pathname]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = response.data.sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );
      setUsers(sorted);
      setStagedUsers(sorted);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching users:", err);
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load users. Please try again.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/accounts/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccounts(response.data);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  const fetchUnions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/company-profile/unions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnions(response.data);
    } catch (err) {
      console.error("Error fetching unions:", err);
    }
  };

  const fetchAppendices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/company-profile/opportunity-appendices`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAppendices(response.data);
    } catch (err) {
      console.error("Error fetching appendices:", err);
    }
  };

  const fetchQuickBooksStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/company-profile/quickbooks-status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setIsQuickBooksConnected(response.data.isQuickBooksConnected || false);
    } catch (err) {
      setIsQuickBooksConnected(false);
    }
  };

  const fetchAutomatedBillingStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/company-profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsAutomatedBillingConnected(
        response.data.plugins?.automatedBilling?.connected || false,
      );
      setBillingAlias(
        response.data.plugins?.automatedBilling?.aliasEmail || null,
      );
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAutomatedLeadsStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/company-profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsAutomatedLeadsConnected(
        response.data.plugins?.automatedLeads?.connected || false,
      );
      setLeadsAlias(response.data.plugins?.automatedLeads?.aliasEmail || null);
      setLeadSources(response.data.plugins?.automatedLeads?.sources || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddClick = () => {
    if (activeTab === "crew") setIsAddingUser(true);
    else if (activeTab === "unions") setIsAddingUnion(true);
    else if (activeTab === "appendices") setIsAddingAppendix(true);
  };

  const handleExportCrew = () => {
    const escapeCsv = (val) => {
      const s = String(val ?? "");
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const headers = [
      "Display Name",
      "Rippling Employee Number",
      "Pay Type",
      "Pay Rate",
      "Worker Classification",
    ];

    const rows = users.map((u) => [
      u.displayName || "",
      u.ripplingEmployeeNumber || "",
      Array.isArray(u.payType) ? u.payType.join("; ") : u.payType || "",
      u.payRate ?? 0,
      u.workerClassification || "",
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\r\n");

    // BOM so Excel opens UTF-8 names correctly
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `crew-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading)
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );

  const isAdmin = currentUser?.role === "admin";
  const visibleTabs = isAdmin
    ? TAB_CONFIG
    : TAB_CONFIG.filter((t) => t.key === "crew");
  const showAddButton = isAdmin && ADD_BUTTON_LABELS[activeTab];

  return (
    <div className="min-h-screen bg-gray-900 text-white mt-2 mx-4 rounded-xl px-4">
      <AlertDialog
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="OK"
        cancelText="Close"
        open={dialog.open}
        onClose={() => setDialog((p) => ({ ...p, open: false }))}
        onConfirm={dialog.onConfirm}
        showCopyButton={dialog.showCopyButton}
        onCopy={dialog.onCopy}
      />

      <div className="container mx-auto  py-2">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white">
              User Management
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage your crew, unions, and system settings
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && activeTab === "crew" && (
              <button
                onClick={handleExportCrew}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-orange-400 text-sm font-semibold px-4 py-2 rounded-lg shadow-sm transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                  />
                </svg>
                Export
              </button>
            )}
            {showAddButton && (
              <button
                onClick={handleAddClick}
                className="flex items-center gap-2 bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {ADD_BUTTON_LABELS[activeTab]}
              </button>
            )}
          </div>
        </div>

        {/* Tab nav */}
        {isAdmin && (
          <div className="flex items-center gap-1 bg-gray-800 border border-gray-700 rounded-xl p-1 mb-6 w-fit">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setCurrentUserPage(1);
                  setCurrentUnionPage(1);
                  setCurrentAppendixPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? "bg-gray-700 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-300 hover:bg-gray-700/50"
                }`}
              >
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.75}
                    d={tab.icon}
                  />
                </svg>
                <span>{tab.label}</span>
                {activeTab === tab.key && (
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Tab content */}
        {activeTab === "crew" && (
          <UserList
            users={users}
            setUsers={setUsers}
            stagedUsers={stagedUsers}
            isAddingUser={isAddingUser}
            setStagedUsers={setStagedUsers}
            accounts={accounts}
            currentUser={currentUser}
            unions={unions}
            onRefresh={fetchUsers}
            allProjects={allProjects}
            setDialog={setDialog}
            navigate={navigate}
            setIsAddingUser={setIsAddingUser}
            currentUserPage={currentUserPage}
            setCurrentUserPage={setCurrentUserPage}
          />
        )}
        {activeTab === "unions" && (
          <UnionManagement
            unions={unions}
            setUnions={setUnions}
            setDialog={setDialog}
            isAddingUnion={isAddingUnion}
            setIsAddingUnion={setIsAddingUnion}
            currentUnionPage={currentUnionPage}
            setCurrentUnionPage={setCurrentUnionPage}
          />
        )}
        {activeTab === "appendices" && (
          <AppendixManagement
            appendices={appendices}
            setAppendices={setAppendices}
            setDialog={setDialog}
            isAddingAppendix={isAddingAppendix}
            setIsAddingAppendix={setIsAddingAppendix}
            currentAppendixPage={currentAppendixPage}
            setCurrentAppendixPage={setCurrentAppendixPage}
          />
        )}
        {activeTab === "plugins" && (
          <PluginsManagement
            isQuickBooksConnected={isQuickBooksConnected}
            setIsQuickBooksConnected={setIsQuickBooksConnected}
            isAutomatedBillingConnected={isAutomatedBillingConnected}
            setIsAutomatedBillingConnected={setIsAutomatedBillingConnected}
            billingAlias={billingAlias}
            setBillingAlias={setBillingAlias}
            isAutomatedLeadsConnected={isAutomatedLeadsConnected}
            setIsAutomatedLeadsConnected={setIsAutomatedLeadsConnected}
            leadsAlias={leadsAlias}
            setLeadsAlias={setLeadsAlias}
            leadSources={leadSources}
            setLeadSources={setLeadSources}
            setDialog={setDialog}
          />
        )}

        <CompanyProfileManagement />
      </div>
    </div>
  );
}
